import { AllGeoJSON } from '@turf/helpers';
import * as turf from '@turf/simplify';
import { Feature } from 'geojson';
import * as wkt from 'wkt';

import { haversine_distance } from '@lib/common/helpers/geography';

export function toGoogleMapDataLayer(
  shape: google.maps.Polygon | google.maps.Circle | google.maps.Polyline | google.maps.Rectangle,
): google.maps.Data {
  const dataLayer = new google.maps.Data();
  if (shape instanceof google.maps.Polygon) {
    dataLayer.add(
      new google.maps.Data.Feature({
        geometry: new google.maps.Data.Polygon([
          shape
            .getPath()
            .getArray()
            .map((latLng) => new google.maps.LatLng(latLng.lat(), latLng.lng())),
        ]),
        properties: {
          type: 'polygon',
        },
      }),
    );
  } else if (shape instanceof google.maps.Polyline) {
    dataLayer.add(
      new google.maps.Data.Feature({
        geometry: new google.maps.Data.LineString(
          shape
            .getPath()
            .getArray()
            .map((latLng) => new google.maps.LatLng(latLng.lat(), latLng.lng())),
        ),
        properties: {
          type: 'line',
        },
      }),
    );
  } else if (shape instanceof google.maps.Circle) {
    const center = shape.getCenter();
    const coordinates = circlePath(center, shape.getRadius(), 360).map(
      (coor) => new google.maps.LatLng(coor[0], coor[1]),
    );
    dataLayer.add(
      new google.maps.Data.Feature({
        properties: {
          center: { lat: center.lat(), lng: center.lng() },
          radius: shape.getRadius(),
          type: 'circle',
        },
        geometry: new google.maps.Data.Polygon([coordinates]),
      }),
    );
  } else if (shape instanceof google.maps.Rectangle) {
    const bounds = shape.getBounds();
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    const points = [
      southWest,
      {
        lat: southWest.lat(),
        lng: northEast.lng(),
      },
      northEast,
      {
        lng: southWest.lng(),
        lat: northEast.lat(),
      },
    ];
    dataLayer.add(
      new google.maps.Data.Feature({
        geometry: new google.maps.Data.Polygon([points]),
        properties: {
          type: 'rectangle',
          bounds: {
            northEast: [northEast.lat(), northEast.lng()],
            southWest: [southWest.lat(), southWest.lng()],
          },
        },
      }),
    );
  }
  return dataLayer;
}

export function circlePath(center: google.maps.LatLng, radius: number, points: number): number[][] {
  const paths = [];
  const factor = 360 / points;
  let distance = 0;
  for (let i = 0; i < points; i += 1, distance += factor) {
    const lat = google.maps.geometry.spherical.computeOffset(center, radius, distance).lat();
    const lng = google.maps.geometry.spherical.computeOffset(center, radius, distance).lng();
    paths.push([lat, lng]);
  }
  return paths;
}

export function processPoints(geometry: any, callback: any, thisArg: any): any {
  if (geometry instanceof google.maps.LatLng) {
    callback.call(thisArg, geometry);
  } else if (geometry instanceof google.maps.Data.Point) {
    callback.call(thisArg, geometry.get());
  } else {
    geometry.getArray().forEach((g) => {
      processPoints(g, callback, thisArg);
    });
  }
}

export function zoomToExtent(
  shape: google.maps.Polygon | google.maps.Circle | google.maps.Rectangle,
  map: google.maps.Map,
): any {
  let bounds = new google.maps.LatLngBounds();
  if (shape instanceof google.maps.Polygon) {
    const points = shape.getPath();
    processPoints(points, bounds.extend, bounds);
  } else if (shape instanceof google.maps.Circle) {
    const circlePoints = circlePath(shape.getCenter(), shape.getRadius(), 360).map(
      (coor) => new google.maps.LatLng(coor[0], coor[1]),
    );
    const geometry = new google.maps.Data.Polygon([circlePoints]);
    processPoints(geometry, bounds.extend, bounds);
  } else if (shape instanceof google.maps.Rectangle) {
    bounds = shape.getBounds();
  }
  map.fitBounds(bounds);
}

export function stringifyGeojson(feature: any): string {
  return wkt.stringify(feature);
}

export function simplifyGeojson(
  geojson: AllGeoJSON | Feature<any>,
  options: {
    tolerance?: number;
    highQuality?: boolean;
    mutate?: boolean;
  },
): AllGeoJSON | Feature<any> {
  return turf.default(geojson, {
    ...options,
  });
}

export function containsPoint(
  shape: google.maps.Polygon | google.maps.Circle,
  point: google.maps.LatLng,
): boolean {
  return shape instanceof google.maps.Polygon
    ? google.maps.geometry.poly.containsLocation(point, shape)
    : shape instanceof google.maps.Circle
    ? pointInCircle(point, shape.getRadius(), shape.getCenter())
    : false;
}

export function pointInCircle(
  point: google.maps.LatLng,
  radius: number,
  center: google.maps.LatLng,
): boolean {
  return google.maps.geometry.spherical.computeDistanceBetween(point, center) <= radius;
}

export function getShapeBounds(
  shape: google.maps.Polygon | google.maps.Circle | google.maps.Rectangle,
): google.maps.LatLngBounds {
  let bounds: google.maps.LatLngBounds;
  if (shape instanceof google.maps.Circle || shape instanceof google.maps.Rectangle) {
    bounds = shape.getBounds();
  } else if (shape instanceof google.maps.Polygon) {
    bounds = new google.maps.LatLngBounds();
    const paths = shape.getPath();
    paths.forEach((path) => {
      bounds.extend(path);
    });
  }
  return bounds;
}

export function convertShapeToCircle(
  shape: google.maps.Polygon | google.maps.Circle,
): google.maps.Circle {
  if (shape instanceof google.maps.Circle) {
    return shape;
  }

  // Get the bounding rectangle of the polygon
  const bounds = new google.maps.LatLngBounds();
  const paths = shape.getPath();
  paths.forEach((path) => {
    bounds.extend(path);
  });
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  // The radius for the circle will be half of the hypotenuse in the bounding box
  // The circle will contain the polygon; there will be areas included in the cirlce that wasn't
  // included in the original polygon selection.
  const radius = haversine_distance(ne, sw) / 2;

  return new google.maps.Circle({
    center: bounds.getCenter(),
    radius,
  });
}

const SQUARE_MILES_IN_SQ_METERS = 2589988.11;
export function getPolygonArea(polygon: google.maps.Polygon): number {
  const path = polygon.getPath();
  const coordinates = path.getArray();

  if (coordinates.length < 3) {
    return 0;
  }

  try {
    const meters = google.maps.geometry.spherical.computeArea(coordinates);
    const area = meters / SQUARE_MILES_IN_SQ_METERS;
    return Number(area.toFixed(2));
  } catch (error) {
    console.error('Error calculating polygon area:', error);
    return 0;
  }
}

export function getPolygonCenter(shape: google.maps.Polygon): google.maps.LatLng {
  const polygonBounds = new google.maps.LatLngBounds();
  shape
    .getPath()
    .getArray()
    .forEach((coords) => {
      polygonBounds.extend(coords);
    });
  return polygonBounds.getCenter();
}

export function arePolygonPathsEqual(
  path1: google.maps.LatLng[],
  path2: google.maps.LatLng[],
): boolean {
  return path1.every((coord, index) => coord.equals(path2[index]));
}
